<template>
    <div
        class="input-component"
        :class="{
            'password-group': type === 'password',
            'email-group': type === 'email',
            'file-group': type === 'file',
            invalid: errors?.length,
            disabled: disabled
        }"
    >
        <input
            v-if="type !== 'file'"
            :type="currentInputType"
            :value="inputValue"
            class="text-h6"
            :class="{ 'not-empty': isNotEmpty }"
            :placeholder="placeholder"
            :disabled="disabled"
            @input="handleInput"
        />
        <label v-if="label.length" class="input-label text-h6 text-grey">{{ label }}</label>

        <div v-if="type === 'file'" class="file-input-wrapper">
            <button class="file-input-button" @click="triggerFileInput">
                {{ fileName ? `Вибраний файл: ${fileName}` : 'Вибрати файл' }}
            </button>
            <input
                ref="fileInput"
                type="file"
                class="file-input"
                :disabled="disabled"
                @change="handleFileChange"
            />
        </div>

        <div
            v-if="type === 'password'"
            class="password-toggler"
            :class="{ toggled: !passwordVisible }"
            @click="togglePasswordVisibility"
        >
            <img src="@/assets/images/eye-closed.svg" class="password-eye eye-closed" alt="" />
            <img src="@/assets/images/eye-opened.svg" class="password-eye eye-opened" alt="" />
        </div>
        <div v-if="errors?.length" class="input-error">
            <span class="text-h7">
                {{ errors }}
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InputComponent',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'text'
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => {}
        },
        mask: {
            type: Object,
            default: () => ({
                number: false,
                precision: 0
            })
        }
    },
    data() {
        return {
            inputValue: this.modelValue,
            passwordVisible: false,
            fileName: null,
            isUpdatingManually: false
        }
    },
    computed: {
        currentInputType() {
            return this.type === 'password' && this.passwordVisible ? 'text' : this.type
        },

        isNotEmpty() {
            return (
                this.inputValue !== '' && this.inputValue !== null && this.inputValue !== undefined
            )
        }
    },
    watch: {
        modelValue(newValue) {
            if (this.isUpdatingManually) {
                this.isUpdatingManually = false

                return
            }

            let value = newValue

            if (this.mask.number) {
                if (value === null) {
                    this.inputValue = value

                    return
                }

                const precision = this.mask.precision ?? 0

                value = value.toString().replace(',', '.')

                if (precision === 0) {
                    value = value.replace(/[^0-9]/g, '')
                    value = value.replace(/^0+(?!$)/, '')
                } else {
                    value = value.replace(/[^0-9.]/g, '')
                    value = value.replace(/(\..*?)\..*/g, '$1')
                    value = value.replace(/^0+(?=\d)/, '')
                    value = value.replace(/^\./, '0.')
                    value = value.replace(new RegExp(`^(\\d*\\.?\\d{0,${precision}}).*`), '$1')
                }
            }

            this.inputValue = value
        }
    },
    methods: {
        handleInput(event) {
            this.isUpdatingManually = true
            let value = event.target.value

            if (this.mask.number) {
                const precision = this.mask.precision ?? 0

                value = value.toString().replace(',', '.')

                if (precision === 0) {
                    value = value.replace(/[^0-9]/g, '')
                    value = value.replace(/^0+(?!$)/, '')
                } else {
                    value = value.replace(/[^0-9.]/g, '')
                    value = value.replace(/(\..*?)\..*/g, '$1')
                    value = value.replace(/^0+(?=\d)/, '')
                    value = value.replace(/^\./, '0.')
                    value = value.replace(new RegExp(`^(\\d*\\.?\\d{0,${precision}}).*`), '$1')
                }
            }

            this.inputValue = value
            this.$emit('update:modelValue', this.inputValue)

            if (this.mask.number) {
                event.target.value = value
            }
        },

        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible
        },

        reset() {
            if (this.type === 'file') {
                this.inputValue = ''
                this.fileName = null
                this.$emit('update:modelValue', '')
            }
        },

        triggerFileInput() {
            this.$refs.fileInput.click()
        },

        handleFileChange(event) {
            const file = event.target.files[0]

            if (file) {
                this.fileName = file.name
                this.$emit('update:modelValue', file)
            }
        }
    }
}
</script>

<style scoped lang="scss">
.input-component {
    position: relative;
    margin-bottom: 1.25rem;
    width: 100%;

    &.invalid {
        input {
            border: 1px solid $red;
        }
    }

    &.email-group,
    &.password-group {
        &:after {
            content: '';
            position: absolute;
            top: 1.375rem;
            left: 0.5rem;
            width: 1.25rem;
            height: 1.25rem;
            background: url('@/assets/images/input-email-icon.svg') no-repeat center center;
            background-size: cover;
            transform: translateY(-50%);
        }

        input {
            padding-left: 2.25rem;
        }
    }

    &.password-group {
        &:after {
            background: url('@/assets/images/input-password-icon.svg') no-repeat center center;
            background-size: cover;
        }
    }

    &.disabled {
        input {
            background: #f3f4f6;
        }
    }

    input {
        width: 100%;
        height: 2.625rem;
        padding: 0.5rem 1rem;
        border-radius: $border-radius-sm;
        outline: none;
        background: $white;
        border: 1px solid $grey-20;

        &.toggled-input {
            padding-right: 4rem;
        }

        &::placeholder {
            color: $grey-100;
        }

        &:disabled {
            user-select: none;
        }

        &:focus {
            border: 1px solid $primary;
        }

        &:focus + .input-label,
        &.not-empty + .input-label {
            top: -0.5rem;
            padding: 0 0.25rem;
            font-size: 0.625rem;
            background: $white;
            border-radius: 0.25rem;
            transform: translateY(0);
        }

        &[type='number'] {
            -moz-appearance: textfield;

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
        }
    }

    .input-label {
        display: flex;
        position: absolute;
        top: 1.375rem;
        left: 1rem;
        color: $grey-100;
        transform: translateY(-50%);
        transition: all ease-in-out 0.3s;
        pointer-events: none;
    }

    .password-toggler {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        display: flex;
        transform: translateY(calc(-50%));
        cursor: pointer;

        &.toggled {
            .eye-opened {
                display: block;
            }

            .eye-closed {
                display: none;
            }
        }

        .password-eye {
            width: 1.25rem;
            height: 1.25rem;
        }

        .eye-opened {
            display: none;
        }
    }

    .input-error {
        padding: 0 1rem;

        span {
            color: $red;
            margin-right: 0.5rem;
        }
    }

    .file-input-wrapper {
        position: relative;
        width: 100%;

        .file-input {
            display: none;
        }

        .file-input-button {
            width: 100%;
            padding: 0.5rem 1rem;
            border-radius: $border-radius-sm;
            background-color: $primary;
            color: $white;
            cursor: pointer;

            &:hover {
                background-color: darken($primary, 5%);
            }

            &:disabled {
                background-color: $grey-20;
            }
        }
    }
}
</style>
